import Button from '@components/button/Button';
import { Dropdown as AntDesignDropdown, Avatar, Badge, MenuProps } from 'antd';
import cx from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MdCheck,
  MdLogout,
  MdOutlineChat,
  MdOutlineSettings,
  MdOutlineSupportAgent,
  MdPerson,
  MdTranslate,
  MdWallpaper,
} from 'react-icons/md';

import styles from './_UserMenu.module.scss';

export const MenuKeys = {
  settings: 'settings',
  logout: 'logout',
  contactSupport: 'contactSupport',
  changeBackground: 'changeBackground',
  contactSales: 'contactSales',
  language: 'language',
  fi: 'fi',
  en: 'en',
};

type IUserMenu = {
  firstName?: string;
  lastName?: string;
  email: string;
  selectedLanguageCode?: string;
  onSelectMenuItem: ({ key }: { key: string }) => void;
  trialDaysRemaining?: number;
  trialTotalDays?: number;
  isTrialExpired?: boolean;
};

const menuStyle: React.CSSProperties = {
  boxShadow: 'none',
  background: 'transparent',
};

const getInitials = (firstName?: string, lastName?: string) => {
  if (!firstName && !lastName) {
    return 'U';
  }
  if (!lastName) {
    return firstName?.slice(0, 2).toUpperCase();
  }
  if (!firstName) {
    return lastName?.slice(0, 2).toUpperCase();
  }
  return `${firstName![0]}${lastName![0]}`.toUpperCase();
};

const UserMenu: React.FC<IUserMenu> = ({
  firstName,
  lastName,
  email,
  selectedLanguageCode,
  onSelectMenuItem,
  trialDaysRemaining,
  trialTotalDays,
  isTrialExpired = false,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'chaosComponents.userMenu',
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [isLanguageSubMenuOpen, setIsLanguageSubMenuOpen] = useState(false);

  const handleOpenChange: MenuProps['onOpenChange'] = useCallback(
    (keys: string[]) =>
      keys.includes(MenuKeys.language)
        ? setIsLanguageSubMenuOpen(true)
        : setIsLanguageSubMenuOpen(false),
    [],
  );

  const handleDropdownOpenChange = useCallback((open: boolean) => {
    setIsMenuOpen(open);
  }, []);

  const firstNameCleaned = useMemo(() => firstName?.trim(), [firstName]);
  const lastNameCleaned = useMemo(() => lastName?.trim(), [lastName]);

  const initials = useMemo(
    () => getInitials(firstNameCleaned, lastNameCleaned),
    [firstNameCleaned, lastNameCleaned],
  );

  type ILanguageLabel = {
    languageCode: string;
    isBetaVersion?: boolean;
  };
  const renderLanguageLabel = useCallback(
    ({ languageCode, isBetaVersion = false }: ILanguageLabel) => {
      const isSelected = languageCode === selectedLanguageCode;

      return (
        <div className="tw-flex tw-items-center tw-gap-2">
          {t(languageCode)}{' '}
          {isBetaVersion && (
            <Badge color="#var(--color-secondary)" count="Beta" size="small" />
          )}
          {isSelected && <MdCheck size={16} className="tw-m-auto" />}
        </div>
      );
    },
    [selectedLanguageCode, t],
  );

  const items: MenuProps['items'] = useMemo(
    () =>
      [
        isTrialExpired && {
          key: MenuKeys.contactSales,
          label: t('contactSales'),
          icon: <MdOutlineSupportAgent size={16} />,
        },
        {
          key: MenuKeys.language,
          label: t('language'),
          icon: <MdTranslate size={16} className="tw-m-auto" />,
          children: isLanguageSubMenuOpen
            ? [
                {
                  key: MenuKeys.en,
                  label: renderLanguageLabel({ languageCode: MenuKeys.en }),
                },
                {
                  key: MenuKeys.fi,
                  label: renderLanguageLabel({
                    languageCode: MenuKeys.fi,
                    isBetaVersion: true,
                  }),
                },
              ]
            : [],
        },
        {
          key: MenuKeys.settings,
          label: t('settings'),
          icon: <MdOutlineSettings size={16} />,
        },
        {
          key: MenuKeys.changeBackground,
          label: t('changeBackground'),
          icon: <MdWallpaper size={16} />,
        },
        {
          key: MenuKeys.contactSupport,
          label: t('contactSupport'),
          icon: <MdOutlineChat size={16} />,
        },
        {
          key: MenuKeys.logout,
          label: t('logout'),
          icon: <MdLogout size={16} />,
        },
      ].filter(Boolean) as MenuProps['items'],
    [isLanguageSubMenuOpen, isTrialExpired, renderLanguageLabel, t],
  );

  const popupRenderer = useCallback(
    (menu: React.ReactNode) => (
      <div
        className={cx(
          styles.userMenuBackdropFilter,
          'tw-max-w-80 tw-bg-[--surface-background] tw-shadow-[--effect-surface-shadow] tw-overflow-hidden tw-border tw-border-solid tw-rounded-[--border-radius-default] tw-border-[--color-border-white]',
        )}
      >
        <div
          className={cx(
            'tw-flex tw-px-3',
            styles.userMenuHeaderBackgroundClass,
          )}
        >
          <div className="tw-m-auto tw-mr-2">
            <Avatar
              size="medium"
              icon={<MdPerson />}
              className="tw-bg-[--color-button-hover]"
            />
          </div>
          <div className="tw-my-2 tw-flex-1 tw-overflow-hidden">
            <div className="tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap">
              {firstNameCleaned} {lastNameCleaned}
            </div>
            <div className="tw-text-[--color-text-weaker] tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap">
              {email}
            </div>
          </div>
        </div>
        {trialDaysRemaining !== undefined && trialTotalDays !== undefined && (
          <div className="tw-px-3 tw-my-2">
            <div
              className="tw-flex tw-flex-col tw-gap-2 tw-px-3 tw-py-2.5 tw-rounded-[--border-radius-small]"
              style={{ background: 'var(--message-background)' }}
            >
              <div className="tw-flex tw-justify-between tw-items-center">
                <span className="tw-text-xs tw-font-semibold tw-text-[--color-text-secondary]">
                  {t('freeTrial')}
                </span>
                <span
                  className={`tw-text-xs tw-font-semibold ${isTrialExpired ? 'tw-text-[--color-status-danger]' : 'tw-text-[--color-secondary]'}`}
                >
                  {isTrialExpired
                    ? t('trialExpired')
                    : t('daysLeft', { count: trialDaysRemaining })}
                </span>
              </div>
              <div className="tw-h-1 tw-rounded-full tw-overflow-hidden tw-bg-[--color-muted]">
                <div
                  className="tw-h-full tw-rounded-full tw-bg-[--color-secondary]"
                  style={{
                    width: `${Math.max(4, ((trialTotalDays - trialDaysRemaining) / trialTotalDays) * 100)}%`,
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>
            </div>
          </div>
        )}
        <div className="tw-px-2 tw-pb-2">
          {React.cloneElement(
            menu as React.ReactElement<{
              style: React.CSSProperties;
            }>,
            { style: menuStyle },
          )}
        </div>
      </div>
    ),
    [
      email,
      firstNameCleaned,
      isTrialExpired,
      lastNameCleaned,
      t,
      trialDaysRemaining,
      trialTotalDays,
    ],
  );

  return (
    <AntDesignDropdown
      destroyOnHidden
      open={isMenuOpen}
      onOpenChange={handleDropdownOpenChange}
      menu={{
        items,
        onClick: onSelectMenuItem,
        onOpenChange: handleOpenChange,
      }}
      popupRender={popupRenderer}
      placement="topLeft"
      classNames={{ root: cx(styles.dropdownClass) }}
    >
      <div>
        <Badge dot={trialDaysRemaining !== undefined} offset={[-4, 4]}>
          <Button type="userMenu" size="large" circle label={initials} />
        </Badge>
      </div>
    </AntDesignDropdown>
  );
};

export default UserMenu;
